import { getMailboxKeyForMailboxInfo } from 'owa-client-types';
import type { MailboxInfo, MailboxKey } from 'owa-client-types';
import { SourceIdPartDelimiter } from 'owa-client-types/lib/SourceIdPartDelimiter';
import { resourceTypeLicense } from 'owa-account-source-list-types/lib/utils/sourceIdResourceTypes';
import getAccountBySourceId from './getAccountBySourceId';
import { accountRankTypeChecker } from '../store/schema/AccountSourceList';

/**
 * Returns the MailboxInfo associated with the specified MailboxKey. This is the inverse of getMailboxKeyForMailboxInfo.
 *
 * See https://outlookweb.visualstudio.com/Outlook%20Web/_wiki/wikis/Outlook%20Web.wiki/15450/Account-Mailbox-vs-Identity-Key
 * for details on how MailboxKey relates to account concepts.
 *
 * @param mailboxKey The MailboxKey for which the MailboxInfo is to be returned
 * @returns The MailboxInfo for the MailboxKey or undefined if it was not found
 */
export default function getMailboxInfoByMailboxKey(
    mailboxKey: MailboxKey
): MailboxInfo | undefined {
    const account = getAccountBySourceId(mailboxKey);

    // getAccountBySourceId resolves a licensing MailboxKey to its coprincipal account, but
    // we want getMailboxInfoByMailboxKey to remain the true inverse of getMailboxKeyForMailboxInfo, so
    // we need special handling for the licensing MailboxInfo case to follow the linkage off the coprincipal account
    if (accountRankTypeChecker.isCoprincipal(account)) {
        const { licensingMailboxInfo } = account;
        const mailboxKeyParts = mailboxKey.split(SourceIdPartDelimiter);
        if (mailboxKeyParts.length > 1 && mailboxKeyParts[1] === resourceTypeLicense) {
            return licensingMailboxInfo &&
                getMailboxKeyForMailboxInfo(licensingMailboxInfo) === mailboxKey
                ? licensingMailboxInfo
                : undefined;
        }
    }

    return account?.mailboxInfo;
}
